/* Chart primitives.
 *
 * Small, dependency-free SVG components for the charts that are not price
 * series (the price chart uses lightweight-charts, which is the right tool for
 * a canvas-backed candle engine and the wrong one for a 60-pixel sparkline).
 *
 * Rules these all follow, because a chart that breaks them misleads:
 *
 *   * Colour follows the ENTITY, never its rank. Filtering a series out must
 *     not repaint the survivors, so every categorical component takes a stable
 *     key and derives its slot from that, not from array position.
 *   * A magnitude ramp is one hue light-to-dark; a signed scale is two hues
 *     around a neutral grey. Never a rainbow.
 *   * Two or more series always carry a legend or direct labels, so identity
 *     is never colour alone. Several of the light-mode steps sit below 3:1 on
 *     white, which makes that a requirement rather than a preference.
 *   * Every chart has a hover layer. An SVG chart in a browser is interactive;
 *     shipping one that is not throws away the only way to read an exact value.
 */
import { useId, useMemo, useState } from 'react'

// The categorical slots, in fixed order. A ninth series is never a generated
// hue — it folds into "Other".
const SERIES_SLOTS = 6

/** Stable slot for an entity. The same symbol keeps the same colour whichever
 *  filter is applied and wherever it appears. */
export function seriesColor(key: string): string {
  let h = 0
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0
  return `var(--series-${(h % SERIES_SLOTS) + 1})`
}

export function seriesColorAt(index: number): string {
  return `var(--series-${(index % SERIES_SLOTS) + 1})`
}

/** Diverging scale for a signed value, normalised against a reference
 *  magnitude. Grey at zero — a hue at the midpoint would imply direction where
 *  there is none. */
export function divergingColor(value: number, maxAbs: number): string {
  if (!Number.isFinite(value) || maxAbs <= 0 || value === 0) return 'var(--div-mid)'
  const t = Math.min(Math.abs(value) / maxAbs, 1)
  const step = t > 0.66 ? 3 : t > 0.33 ? 2 : 1
  return value > 0 ? `var(--div-pos-${step})` : `var(--div-neg-${step})`
}

/** Sequential ramp for unsigned magnitude. */
export function sequentialColor(t: number): string {
  const clamped = Math.max(0, Math.min(1, t))
  return `var(--seq-${Math.min(5, Math.floor(clamped * 5) + 1)})`
}

// ---------------------------------------------------------------------------
// Sparkline
// ---------------------------------------------------------------------------

export function Sparkline({
  values, width = 84, height = 24, tone,
}: {
  values: number[]
  width?: number
  height?: number
  /** Colour by direction. Omit for a neutral accent line. */
  tone?: 'auto' | 'neutral'
}) {
  const clean = values.filter((v) => Number.isFinite(v))
  if (clean.length < 2) {
    return <svg width={width} height={height} role="presentation" />
  }

  const min = Math.min(...clean)
  const max = Math.max(...clean)
  const span = max - min || 1
  const dx = width / (clean.length - 1)
  const y = (v: number) => height - 2 - ((v - min) / span) * (height - 4)

  const d = clean.map((v, i) => `${i === 0 ? 'M' : 'L'}${(i * dx).toFixed(2)},${y(v).toFixed(2)}`).join(' ')
  const rising = clean[clean.length - 1]! >= clean[0]!
  const stroke = tone === 'neutral'
    ? 'var(--text-muted)'
    : rising ? 'var(--profit)' : 'var(--loss)'

  return (
    <svg width={width} height={height} aria-hidden style={{ display: 'block' }}>
      <path d={d} fill="none" stroke={stroke} strokeWidth={1.5}
            strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  )
}

// ---------------------------------------------------------------------------
// Donut — composition of a whole
// ---------------------------------------------------------------------------

export interface Slice { key: string; label: string; value: number }

export function Donut({
  slices, size = 150, thickness = 22, centerLabel, centerValue, formatValue,
}: {
  slices: Slice[]
  size?: number
  thickness?: number
  centerLabel?: string
  centerValue?: string
  formatValue?: (v: number) => string
}) {
  const [hover, setHover] = useState<string | null>(null)
  const total = slices.reduce((a, s) => a + Math.abs(s.value), 0)

  if (total <= 0) {
    return (
      <div style={{
        width: size, height: size, display: 'grid', placeItems: 'center',
        color: 'var(--text-muted)', fontSize: 'var(--fs-xs)',
      }}>
        No positions
      </div>
    )
  }

  const r = (size - thickness) / 2
  const c = size / 2
  const circumference = 2 * Math.PI * r
  let offset = 0

  // Segments are separated by a 2px surface gap so adjacent slices never blur
  // into one another — the same reason bars carry a gap.
  const gap = 2

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        {slices.map((s) => {
          const frac = Math.abs(s.value) / total
          const len = Math.max(circumference * frac - gap, 0.5)
          const el = (
            <circle
              key={s.key}
              cx={c} cy={c} r={r}
              fill="none"
              stroke={seriesColor(s.key)}
              strokeWidth={hover === s.key ? thickness + 3 : thickness}
              strokeDasharray={`${len} ${circumference - len}`}
              strokeDashoffset={-offset}
              onMouseEnter={() => setHover(s.key)}
              onMouseLeave={() => setHover(null)}
              style={{ cursor: 'pointer', transition: 'stroke-width 100ms' }}
            />
          )
          offset += circumference * frac
          return el
        })}
      </svg>

      <div style={{
        position: 'absolute', inset: 0, display: 'grid', placeItems: 'center',
        textAlign: 'center', pointerEvents: 'none',
      }}>
        {hover ? (
          <div>
            <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>
              {slices.find((s) => s.key === hover)?.label}
            </div>
            <div className="num" style={{ fontSize: 'var(--fs-md)', fontWeight: 700 }}>
              {formatValue
                ? formatValue(slices.find((s) => s.key === hover)!.value)
                : slices.find((s) => s.key === hover)!.value.toFixed(2)}
            </div>
            <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-secondary)' }}>
              {((Math.abs(slices.find((s) => s.key === hover)!.value) / total) * 100).toFixed(1)}%
            </div>
          </div>
        ) : (
          <div>
            {centerLabel && (
              <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>
                {centerLabel}
              </div>
            )}
            {centerValue && (
              <div className="num" style={{ fontSize: 'var(--fs-lg)', fontWeight: 700 }}>
                {centerValue}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/** The donut's legend. Always rendered beside it: several light-mode steps sit
 *  below 3:1 on white, so identity must not rest on colour alone. */
export function DonutLegend({
  slices, formatValue,
}: {
  slices: Slice[]
  formatValue?: (v: number) => string
}) {
  const total = slices.reduce((a, s) => a + Math.abs(s.value), 0) || 1
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
      {slices.map((s) => (
        <div key={s.key} style={{
          display: 'grid', gridTemplateColumns: '9px 1fr auto auto',
          alignItems: 'center', gap: 'var(--sp-2)', fontSize: 'var(--fs-xs)',
        }}>
          <span style={{
            width: 9, height: 9, borderRadius: 2,
            background: seriesColor(s.key), display: 'inline-block',
          }} />
          <span style={{
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            color: 'var(--text-secondary)',
          }}>
            {s.label}
          </span>
          <span className="num" style={{ color: 'var(--text-primary)' }}>
            {formatValue ? formatValue(s.value) : s.value.toFixed(2)}
          </span>
          <span className="num" style={{ color: 'var(--text-muted)', width: 42, textAlign: 'right' }}>
            {((Math.abs(s.value) / total) * 100).toFixed(1)}%
          </span>
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Horizontal bar series — signed magnitude, ranked
// ---------------------------------------------------------------------------

export interface BarDatum { key: string; label: string; value: number; sub?: string }

export function SignedBars({
  data, format, height = 20, maxRows,
}: {
  data: BarDatum[]
  format?: (v: number) => string
  height?: number
  maxRows?: number
}) {
  const [hover, setHover] = useState<string | null>(null)
  const rows = maxRows ? data.slice(0, maxRows) : data
  const maxAbs = Math.max(...rows.map((d) => Math.abs(d.value)), 1e-9)

  if (rows.length === 0) {
    return <Empty>Nothing to show</Empty>
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {rows.map((d) => {
        const pct = (Math.abs(d.value) / maxAbs) * 50   // half-width each side
        const positive = d.value >= 0
        return (
          <div
            key={d.key}
            onMouseEnter={() => setHover(d.key)}
            onMouseLeave={() => setHover(null)}
            style={{
              display: 'grid', gridTemplateColumns: '90px 1fr 76px',
              alignItems: 'center', gap: 'var(--sp-2)',
              fontSize: 'var(--fs-xs)',
              background: hover === d.key ? 'var(--bg-hover)' : undefined,
              borderRadius: 'var(--radius-sm)',
            }}
          >
            <span style={{
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              color: 'var(--text-secondary)',
            }}>
              {d.label}
            </span>

            <div style={{ position: 'relative', height, background: 'transparent' }}>
              {/* The zero line is drawn explicitly: on a signed bar chart the
                  baseline is the reference the reader measures against, and an
                  implied one is easy to misplace by a few pixels. */}
              <div style={{
                position: 'absolute', left: '50%', top: 0, bottom: 0,
                width: 1, background: 'var(--border-strong)',
              }} />
              <div style={{
                position: 'absolute', top: 3, bottom: 3,
                left: positive ? '50%' : `${50 - pct}%`,
                width: `${pct}%`,
                background: positive ? 'var(--profit)' : 'var(--loss)',
                // 4px rounded data-end, square against the baseline.
                borderRadius: positive ? '0 3px 3px 0' : '3px 0 0 3px',
                minWidth: 2,
              }} />
            </div>

            <span className="num" style={{
              textAlign: 'right',
              color: positive ? 'var(--profit)' : 'var(--loss)',
            }}>
              {format ? format(d.value) : d.value.toFixed(2)}
            </span>
          </div>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Treemap-style heatmap — magnitude by area, direction by colour
// ---------------------------------------------------------------------------

export interface HeatCell {
  key: string
  label: string
  /** Drives the colour. */
  value: number
  /** Drives the area. Falls back to equal weighting when absent. */
  weight?: number
  sub?: string
}

export function Heatmap({
  cells, onSelect, minCellPx = 54,
}: {
  cells: HeatCell[]
  onSelect?: (key: string) => void
  minCellPx?: number
}) {
  const [hover, setHover] = useState<string | null>(null)
  const maxAbs = useMemo(
    () => Math.max(...cells.map((c) => Math.abs(c.value)), 1e-9), [cells])

  if (cells.length === 0) return <Empty>No data</Empty>

  const totalWeight = cells.reduce((a, c) => a + (c.weight ?? 1), 0) || 1

  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: 2, alignContent: 'flex-start',
    }}>
      {cells.map((c) => {
        const share = (c.weight ?? 1) / totalWeight
        // Area proportional to weight, floored so a small cell stays readable.
        // A cell too small to label is worse than a slightly wrong area.
        const basis = Math.max(minCellPx, Math.sqrt(share) * 320)
        return (
          <button
            key={c.key}
            onClick={() => onSelect?.(c.key)}
            onMouseEnter={() => setHover(c.key)}
            onMouseLeave={() => setHover(null)}
            title={`${c.label}: ${c.value >= 0 ? '+' : ''}${c.value.toFixed(2)}%`}
            style={{
              flex: `1 1 ${basis}px`, minWidth: minCellPx, height: 58,
              background: divergingColor(c.value, maxAbs),
              border: hover === c.key
                ? '2px solid var(--text-primary)'
                : '2px solid transparent',
              borderRadius: 'var(--radius-sm)',
              display: 'flex', flexDirection: 'column',
              alignItems: 'flex-start', justifyContent: 'center',
              padding: '4px 6px', gap: 1,
              cursor: onSelect ? 'pointer' : 'default',
              overflow: 'hidden', textAlign: 'left',
            }}
          >
            {/* Direct labels, always. The diverging steps alone would leave a
                colourblind reader guessing at direction, and the sign does not. */}
            <span style={{
              fontSize: 'var(--fs-2xs)', fontWeight: 700, color: '#fff',
              maxWidth: '100%', overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              textShadow: '0 1px 2px rgba(0,0,0,0.55)',
            }}>
              {c.label}
            </span>
            <span className="num" style={{
              fontSize: 'var(--fs-xs)', fontWeight: 700, color: '#fff',
              textShadow: '0 1px 2px rgba(0,0,0,0.55)',
            }}>
              {c.value >= 0 ? '+' : ''}{c.value.toFixed(2)}%
            </span>
            {c.sub && (
              <span style={{
                fontSize: 9, color: 'rgba(255,255,255,0.85)',
                textShadow: '0 1px 2px rgba(0,0,0,0.55)',
              }}>
                {c.sub}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Column chart — a value per period, signed
// ---------------------------------------------------------------------------

export function Columns({
  data, height = 90, format,
}: {
  data: { key: string; label: string; value: number }[]
  height?: number
  format?: (v: number) => string
}) {
  const [hover, setHover] = useState<number | null>(null)
  const id = useId()
  if (data.length === 0) return <Empty>No data</Empty>

  const maxAbs = Math.max(...data.map((d) => Math.abs(d.value)), 1e-9)
  const zeroY = height / 2

  return (
    <div style={{ position: 'relative' }}>
      <div style={{
        display: 'flex', alignItems: 'stretch', gap: 2, height,
        borderBottom: 'none',
      }}>
        {data.map((d, i) => {
          const h = (Math.abs(d.value) / maxAbs) * (height / 2 - 4)
          const positive = d.value >= 0
          return (
            <div
              key={d.key}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              style={{ flex: 1, position: 'relative', minWidth: 3, cursor: 'pointer' }}
            >
              <div style={{
                position: 'absolute', left: 0, right: 0,
                top: positive ? zeroY - h : zeroY,
                height: Math.max(h, 1),
                background: positive ? 'var(--profit)' : 'var(--loss)',
                opacity: hover === null || hover === i ? 1 : 0.45,
                borderRadius: positive ? '3px 3px 0 0' : '0 0 3px 3px',
              }} />
            </div>
          )
        })}
      </div>

      {/* Baseline drawn over the columns so it is never obscured. */}
      <div style={{
        position: 'absolute', left: 0, right: 0, top: zeroY,
        height: 1, background: 'var(--border-strong)', pointerEvents: 'none',
      }} />

      <div style={{
        height: 16, fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)',
        display: 'flex', justifyContent: 'space-between', paddingTop: 2,
      }}>
        {hover !== null ? (
          <>
            <span>{data[hover]!.label}</span>
            <span className="num" style={{
              color: data[hover]!.value >= 0 ? 'var(--profit)' : 'var(--loss)',
            }}>
              {format ? format(data[hover]!.value) : data[hover]!.value.toFixed(2)}
            </span>
          </>
        ) : (
          <>
            <span>{data[0]?.label}</span>
            <span>{data[data.length - 1]?.label}</span>
          </>
        )}
      </div>
      <span id={id} hidden />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Meter — one bounded value against a limit
// ---------------------------------------------------------------------------

export function Meter({
  value, max, label, format, warnAt = 0.75, criticalAt = 0.9,
}: {
  value: number
  max: number
  label: string
  format?: (v: number) => string
  warnAt?: number
  criticalAt?: number
}) {
  const frac = max > 0 ? Math.min(value / max, 1) : 0
  // Status colours, reserved for state and never reused for a series.
  const tone = frac >= criticalAt ? 'var(--danger)'
             : frac >= warnAt ? 'var(--warn)'
             : 'var(--ok)'
  const state = frac >= criticalAt ? 'critical' : frac >= warnAt ? 'warning' : 'ok'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)',
      }}>
        <span style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
        <span className="num" style={{ color: tone }}>
          {/* The state is spelled out, not left to colour: a red bar and an
              amber one are the same bar to a colourblind reader. */}
          {format ? format(value) : value.toFixed(2)} · {(frac * 100).toFixed(0)}% {state}
        </span>
      </div>
      <div style={{
        height: 6, background: 'var(--bg-input)', borderRadius: 3,
        border: '1px solid var(--border)', overflow: 'hidden',
      }}>
        <div style={{
          width: `${frac * 100}%`, height: '100%', background: tone,
          borderRadius: 3, transition: 'width 200ms',
        }} />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------

export function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      display: 'grid', placeItems: 'center', padding: 'var(--sp-6)',
      color: 'var(--text-muted)', fontSize: 'var(--fs-xs)', textAlign: 'center',
    }}>
      {children}
    </div>
  )
}
